import DataTable from '@/components/features/data/DataTable'
import { grantColumns } from '@/components/features/data/DataTable/ColumnDefinitions/GrantColumns'
import { ORG_ROLE_OPTIONS } from '@/components/features/organisations/groups/groupSetup'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import {
  PaginationAction,
  UseCursorPaginationQueryOptions,
  useCursorPaginationQuery,
} from '@/hooks/useCursorPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { getTablePageSize } from '@/hooks/useUserPreferences'
import { fetchGrantsPaginated, fetchGroups } from '@/lib/clientHelpers'
import { getGroupName, getUserFullName } from '@/lib/utils'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { Select } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import type { Group } from '@droidmap/organisation-service-contract'
import {
  Grant,
  GrantPrincipalType,
  GrantResourceType,
  Role,
} from '@droidmap/permissions-service-contract'

const PREFERENCE_KEY = 'ORGANISATION_GRANTS'

/**
 * Generate a unique ID for a Grant for use in the DataTable.
 */
const getGrantId = (
  grant: Pick<Grant, 'resourceId' | 'principalId' | 'role'>,
): string => {
  return `resource:${grant.resourceId}#principal:${grant.principalId}:role:${grant.role}`
}

export const Route = createFileRoute('/_authenticated/orgs/$orgId/permissions')(
  {
    beforeLoad: ({ context, params }) => {
      const queryOptions: UseCursorPaginationQueryOptions<
        Grant & { id: string }
      > = {
        preferenceKey: PREFERENCE_KEY,
        queryKey: ['organisationGrants', params.orgId],
        queryFn: async (options) => {
          const result = await fetchGrantsPaginated(
            params.orgId,
            context.client.permissionsClient,
            { ...context.metadata, orgId: params.orgId },
            options,
          )
          return {
            items: result.items.map((g) => ({ ...g, id: getGrantId(g) })),
            next: result.next,
          }
        },
        initialPageSize: getTablePageSize(PREFERENCE_KEY),
        enabled: !!params.orgId,
      }

      return {
        grantsQueryOptions: queryOptions,
      }
    },
    component: Permissions,
    loader: async ({ context }) => {
      const { grantsQueryOptions } = context
      const pageSize: number = grantsQueryOptions.initialPageSize ?? 10

      // Prefetch first page - data will be cached and used by the hook
      await context.queryClient.fetchQuery({
        queryKey: [...grantsQueryOptions.queryKey, pageSize, 0],
        queryFn: () =>
          grantsQueryOptions.queryFn({
            limit: pageSize,
          }),
      })

      return { crumb: 'Permissions', metadata: context.metadata }
    },
  },
)

function Permissions() {
  const { orgId } = Route.useParams()
  const { metadata, grantsQueryOptions } = Route.useRouteContext()

  const { permissionsClient, organisationClient, userClient } = useClient()

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const { data: groups = [] } = useQuery({
    queryKey: ['groups', orgId],
    queryFn: () => fetchGroups(organisationClient, orgId, metadata),
  })

  // Transform groups array to a groupId -> group map
  const groupMap = groups.reduce<Record<string, Group>>((acc, group) => {
    acc[group.id] = group
    return acc
  }, {})

  // Fetch organisation-level grants with pagination using query options from beforeLoad
  const {
    data: grants,
    isLoading: isLoadingGrants,
    isError,
    pagination,
    refresh,
  } = useCursorPaginationQuery(grantsQueryOptions)

  const [createGrantOpen, setCreateGrantOpen] = useState(false)
  const [selectedPrincipalType, setSelectedPrincipalType] =
    useState<GrantPrincipalType>(GrantPrincipalType.USER)
  const [selectedPrincipalId, setSelectedPrincipalId] = useState('')
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined)
  const [createGrantError, setCreateGrantError] = useState<string | null>(null)
  const [isCreatingGrant, setIsCreatingGrant] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [grantToDelete, setGrantToDelete] = useState<
    (Grant & { id: string }) | undefined
  >(undefined)

  const createGrantMutation = useMutation({
    mutationFn: async (event: React.FormEvent) => {
      event.preventDefault()
      setCreateGrantError(null)
      setIsCreatingGrant(true)
      if (!selectedPrincipalId || !selectedRole) {
        setCreateGrantError('Please select a principal and a role.')
        setIsCreatingGrant(false)
        return
      }
      try {
        const grant = await permissionsClient.createGrant(
          {
            orgId,
            principalId: selectedPrincipalId,
            principalType: selectedPrincipalType,
            resourceId: orgId,
            resourceType: GrantResourceType.ORGANISATION,
            role: selectedRole,
          },
          metadata,
        )
        return grant
      } catch (err) {
        setCreateGrantError(
          err instanceof Error ? err.message : 'Failed to create grant.',
        )
        setIsCreatingGrant(false)
        throw err
      }
    },
    onSuccess: (grant) => {
      setCreateGrantOpen(false)
      setSelectedPrincipalId('')
      setSelectedRole(undefined)
      setIsCreatingGrant(false)
      if (grant) {
        refresh({
          action: PaginationAction.CREATE,
          item: {
            ...grant,
            id: getGrantId(grant),
          },
        })
      }
    },
    onError: () => {
      setIsCreatingGrant(false)
    },
  })

  const deleteGrantMutation = useMutation({
    mutationFn: async () => {
      if (!grantToDelete) return
      await permissionsClient.deleteGrant({
        principalId: grantToDelete.principalId,
        resourceId: grantToDelete.resourceId,
        resourceType: grantToDelete.resourceType,
        role: grantToDelete.role,
      })
    },
    onSuccess: () => {
      if (grantToDelete) {
        refresh({ action: PaginationAction.DELETE, item: grantToDelete })
      }
      setDeleteDialogOpen(false)
      setGrantToDelete(undefined)
    },
  })

  return (
    <div>
      {/* Grants Table */}
      <DataTable
        data={grants}
        columns={grantColumns}
        title="Organisation Grants"
        description="Organisation level grants for this organisation."
        isLoading={isLoadingGrants}
        onCreate={() => setCreateGrantOpen(true)}
        paginationConfig={pagination}
        tableOptions={{
          meta: {
            userMap,
            groupMap,
            hasError: isError,
            actions: {
              onDelete: (grant: Grant & { id: string }) => {
                setGrantToDelete(grant)
                setDeleteDialogOpen(true)
              },
            },
          },
        }}
      />
      {isError && (
        <div className="text-text mt-4">
          Failed to load organisation grants.
        </div>
      )}

      {/* Create Grant Modal */}
      <Modal
        title="Create Organisation Grant"
        description="Assign a role to an organisation member or group."
        icon={<PlusCircleIcon />}
        isOpen={createGrantOpen}
        onClose={() => {
          setCreateGrantOpen(false)
          setSelectedPrincipalId('')
          setSelectedRole(undefined)
          setCreateGrantError(null)
          setIsCreatingGrant(false)
        }}
      >
        <form onSubmit={createGrantMutation.mutate} className="space-y-6">
          <Select
            label="Principal Type"
            value={selectedPrincipalType}
            onChange={(e) => {
              setSelectedPrincipalType(e.target.value as GrantPrincipalType)
              setSelectedPrincipalId('')
            }}
          >
            <option value="user">Organisation Member</option>
            <option value="group">Organisation Group</option>
          </Select>
          <Select
            label={
              selectedPrincipalType === 'user'
                ? 'Select Member'
                : 'Select Group'
            }
            value={selectedPrincipalId}
            onChange={(e) => setSelectedPrincipalId(e.target.value)}
          >
            <option value="">-- Select --</option>
            {selectedPrincipalType === 'user'
              ? Object.entries(userMap).map(([userId]) => (
                  <option key={userId} value={userId}>
                    {getUserFullName(userMap, userId) || userId}
                  </option>
                ))
              : Object.entries(groupMap).map(([groupId]) => (
                  <option key={groupId} value={groupId}>
                    {getGroupName(groupMap, groupId) || groupId}
                  </option>
                ))}
          </Select>
          <Select
            label="Role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as Role)}
          >
            <option value="">-- Select Role --</option>
            {ORG_ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
          {createGrantError && (
            <div className="text-text text-sm">{createGrantError}</div>
          )}
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button
              type="submit"
              variant="creative"
              disabled={
                !selectedPrincipalId || !selectedRole || isCreatingGrant
              }
              isLoading={isCreatingGrant}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateGrantOpen(false)
                setSelectedPrincipalId('')
                setSelectedRole(undefined)
                setCreateGrantError(null)
                setIsCreatingGrant(false)
              }}
              disabled={isCreatingGrant}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title="Remove Organisation Grant"
        description="You're about to remove this permission grant. The grantee will lose the granted access immediately. Grants can be recreated if needed."
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setGrantToDelete(undefined)
        }}
        onConfirm={() => deleteGrantMutation.mutate()}
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteGrantMutation.isPending}
      />
    </div>
  )
}
